import React, { useEffect, useRef, useState } from "react";
import OdxIcon from "../Odx-Icon";
import OdxDropdown from "../Odx-Dropdown";
import { odxIconSets } from "../../constants";
import { OdxInputError } from "./Interfaces";
import {simplifyForHtmlId} from "../../../Utils/transformer";

interface OdxSelectProps extends React.HTMLProps<HTMLSelectElement> {
    options: OdxSelectOptionProps[];
    value: string | string[];
    onChange: (event: React.ChangeEvent<HTMLSelectElement> | string[]) => void;
    placeholder: string;
    disabled?: boolean;
    className?: string;
    isLoading?: boolean;
    id?: string;
    label?: string;
    isRequired?: boolean;
    error?: OdxInputError;
    isMulti?: boolean;
    onBlur?: () => void;
    matchReferenceWidth?: boolean;
    showMultiOptions?: boolean;
}

export interface OdxSelectOptionProps extends React.HTMLProps<HTMLOptionElement> {
    value: string;
    label: string;
    disabled?: boolean;
    className?: string;
}

/**
 * Select-Component. Zeigt ein Dropdown mit Options an.
 */
const OdxSelect: React.FC<OdxSelectProps> = ({
                                                 options = [],
                                                 value,
                                                 onChange,
                                                 placeholder,
                                                 disabled = false,
                                                 className = "",
                                                 isLoading,
                                                 id = "undefined",
                                                 label,
                                                 isRequired = false,
                                                 error,
                                                 isMulti = false,
                                                 onBlur,
                                                 matchReferenceWidth = true,
                                                 showMultiOptions = false,
                                                 ...rest
                                             }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen && onBlur) {
            onBlur(); // todo: warum wird dies initial mit einer Verzögerung aufgerufen?
        }
    }, [isOpen]);

    const selectedValues = isMulti
        ? Array.isArray(value)
            ? value
            : []
        : typeof value === "string"
            ? value
            : "";

    const handleSelect = (optionValue: string, event?: React.MouseEvent<HTMLDivElement>) => {
        event?.stopPropagation();
        if (disabled) return;

        if (isMulti) {
            let newValues = Array.isArray(selectedValues) ? [...selectedValues] : [];
            if (newValues.includes(optionValue)) {
                newValues = newValues.filter((val) => val !== optionValue);
            } else {
                newValues.push(optionValue);
            }
            onChange(newValues);
        } else {
            const syntheticEvent = {
                target: { value: optionValue } as HTMLSelectElement,
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange(syntheticEvent);
            setIsOpen(false);
        }
    };

    const handleDropdownToggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (disabled) return;
        setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Handler für "Select all"
    const handleSelectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (disabled) return;

        if (isMulti) {
            // Bei Multi-Select: Alle nicht deaktivierten Optionen auswählen
            const allOptionValues = options.filter(option => !option.disabled).map(option => option.value);
            onChange(allOptionValues);
        } else {
            // Bei Single-Select: Wähle die erste verfügbare Option (sofern vorhanden)
            const availableOption = options.find(option => !option.disabled);
            if (availableOption) {
                const syntheticEvent = {
                    target: { value: availableOption.value },
                } as React.ChangeEvent<HTMLSelectElement>;
                onChange(syntheticEvent);
            }
            setIsOpen(false);
        }
    };

    // Handler für "Clear"
    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (disabled) return;

        if (isMulti) {
            onChange([]);
        } else {
            const syntheticEvent = {
                target: { value: "" },
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange(syntheticEvent);
            setIsOpen(false);
        }
    };

    // Dropdown-Inhalt unterteilt in eine scrollbare Optionsliste und einen fixen
    // Bereich für die Aktions-Buttons, sodass diese immer sichtbar sind.
    const dropdownContent = (
        <div className="odx-select-dropdown-content">
            {showMultiOptions && <><div
                className="odx-select-options__actions"
                style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem" }}
            >
                <button
                    type="button"
                    className="odx-select-options__button select-all"
                    onClick={handleSelectAll}
                    disabled={disabled}
                >
                    Select all
                </button>
                <button
                    type="button"
                    className="odx-select-options__button clear"
                    onClick={handleClear}
                    disabled={disabled}
                >
                    Clear
                </button>
            </div>
            <odx-separator style={{margin: '1rem 0'}} />
            </>
            }
            <div role="listbox" className="odx-dropdown__option-list">
                {options.map((option) => {
                    if (!option) return null;

                    const isSelected = isMulti
                        ? (selectedValues as string[]).includes(option.value)
                        : option.value === selectedValues;

                    return (
                        <div
                            key={option.value || Math.random()} // todo: Math.random() vermeiden
                            role="option"
                            className={`odx-select-option odx-option ${option.disabled ? "is-disabled" : ""} ${
                                isSelected ? "is-active" : ""
                            }`}
                            aria-selected={isSelected}
                            id={"odxSelectDropDownOption_" + id + "_" + simplifyForHtmlId((option.value || option.label))}
                            aria-disabled={option.disabled}
                            onClick={(event) => {
                                if (!option.disabled) handleSelect(option.value, event);
                            }}
                        >
                            {option.label}
                            {isMulti && isSelected && (
                                <OdxIcon
                                    name="check"
                                    size="sm"
                                    iconSet={odxIconSets.core}
                                    className="odx-select-option__check-icon"
                                />
                            )}
                        </div>
                    );
                })}
            </div>

        </div>
    );

    const renderPlaceholder = () => {
        if (isMulti) {
            if ((selectedValues as string[]).length === 0) {
                return placeholder;
            }
            return (selectedValues as string[])
                .map((val) => options.find((option) => option.value === val)?.label)
                .join(", ");
        } else {
            if (!selectedValues) {
                return placeholder;
            }
            return options.find((option) => option.value === selectedValues)?.label || placeholder;
        }
    };

    const showValue = () => {
        if (isMulti) {
            if ((selectedValues as string[]).length === 0) {
                return placeholder;
            }
            // Zeigt das erste Element und, falls mehr als drei Elemente ausgewählt sind, "..." an
            return (selectedValues as string[])
                .map((val) => options.find((option) => option.value === val)?.label)
                .slice(0, 1)
                .join(", ") + ((selectedValues as string[]).length > 3 ? "..." : "");
        } else {
            return selectedValues || placeholder;
        }
    };

    return (
        <div
            className={`odx-form-field--full odx-form-field relative ${disabled ? "is-disabled" : ""} ${
                isRequired ? "odx-form-field--required" : ""
            } ${className}`}
            ref={containerRef}
        >
            {label && (
                <label
                    className="odx-form-field__label"
                    htmlFor={"odx-form-field-" + id}
                    aria-owns={"odx-form-field-" + id}
                >
                    <span className="odx-form-field-label">{label}</span>
                    {isRequired && <span className="odx-form-field__label-is-required">*</span>}
                </label>
            )}
            <div className="odx-form-field__inner">
                <div className="odx-form-field__control">
                    <div className="odx-form-field__prefix"></div>
                    <div
                        className={`odx-select odx-form-field-control ${disabled ? "is-disabled" : ""} ${
                            isLoading ? "is-loading" : ""
                        }`}
                        id={"odx-form-field-" + id}
                        aria-describedby={"odx-form-field-" + id + "-hint"}
                        tabIndex={0}
                        aria-multiselectable={isMulti}
                        onClick={handleDropdownToggle}
                    >
                        <div
                            aria-haspopup="true"
                            className="odx-select__trigger odx-dropdown-host"
                            aria-expanded={isOpen}
                        >
                            {((isMulti && (selectedValues as string[]).length > 0) ||
                                (!isMulti && selectedValues)) && (
                                <div id={"OdxSelectValue_" + id} className="odx-select__value">
                                    {showValue()}
                                </div>
                            )}
                            {(!value ||
                                (Array.isArray(value) && value.length === 0)) && (
                                <div className="odx-select__placeholder">{renderPlaceholder()}</div>
                            )}
                            <OdxIcon
                                name="chevron-down"
                                size="md"
                                iconSet={odxIconSets.core}
                                className="odx-select__indicator odx-icon--medium notranslate"
                            />
                        </div>
                    </div>
                    <div className="odx-form-field__suffix"></div>
                </div>
            </div>
            <OdxDropdown
                content={dropdownContent}
                position="bottom"
                matchReferenceWidth={matchReferenceWidth}
                offset={0}
                showArrow={false}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                buttonRef={containerRef}
            />
            {error && error.isActive && (
                <div className="odx-form-field__info">
                    <span className="odx-form-field-error">{error.text}</span>
                </div>
            )}
        </div>
    );
};

export default OdxSelect;
